import { RefObject, useCallback, useEffect, useState } from "react";
import { animate } from "framer-motion";
// @ts-ignore
import createTransition from "gl-transition";

import { createGL } from "../utils/createGL";
import { shader } from "../utils/shader";
import { GlTransitionProps } from "./useGlTransitionTypes";
import { loadTexture } from "../utils/loadTexture";
import { requestCORSIfNotSameOrigin } from "../utils/requestCORSIfNotSameOrigin";
import { DYNAMIC_BASE_URL } from "shared/enum";

export function useGlTransition(
  canvasRef: RefObject<HTMLCanvasElement>,
  transition: string,
  reverse: boolean,
  texture?: any,
): any {
  const [state, setState] = useState<GlTransitionProps>({
    from: texture,
    transition,
    texture,
    reverse,
  });

  useEffect(() => {
    const canvas: HTMLCanvasElement | null = canvasRef.current;
    const gl = createGL(canvas);
    const url = `${DYNAMIC_BASE_URL}/resources/fallback.png`;

    const image = new Image();
    requestCORSIfNotSameOrigin(image, url);
    image.onload = () => {
      const fallbackTexture = loadTexture(image, gl);
      setState((s) => ({ ...s, gl, from: fallbackTexture }));
    };
    image.onerror = () => {
      console.warn("Image could not be loaded");
    };
    image.src = url;
  }, []);

  useEffect(() => {
    if (state.gl && state.from && state.texture && state.transition) {
      const newTexture = loadTexture(state.texture, state.gl);
      setState((s) => ({ ...s, to: newTexture }));
      startAnimation(newTexture);
    }
  }, [state.texture, state.gl]);

  const startAnimation = (_texture: any) => {
    if (state.motionTransition?.duration !== 0) {
      animate(0, 1, {
        type: state.motionTransition?.duration ? undefined : "spring",
        stiffness: state.motionTransition?.stiffness ? state.motionTransition.stiffness : 400,
        damping: state.motionTransition?.damping ? state.motionTransition.damping : 80,
        duration: state.motionTransition?.duration ? state.motionTransition.duration : undefined,

        onUpdate: (latest) => {
          const transitionWebGL = createTransition(
            state.gl,
            shader.find((t: any) => t.name === state.transition),
          );
          transitionWebGL.draw(
            state.reverse ? 1 - latest : latest,
            state.reverse ? _texture : state.from,
            state.reverse ? state.from : _texture,
            canvasRef.current?.width,
            canvasRef.current?.height,
            {
              persp: 0.0,
              unzoom: 0.0,
            },
          );
        },
      });
    } else {
      const transitionWebGL = createTransition(
        state.gl,
        shader.find((t: any) => t.name === state.transition),
      );
      transitionWebGL.draw(
        state.reverse ? 0 : 1,
        state.reverse ? _texture : state.from,
        state.reverse ? state.from : _texture,
        canvasRef.current?.width,
        canvasRef.current?.height,
        {
          persp: 0.0,
          unzoom: 0.0,
        },
      );
    }
    setState((s) => ({ ...s, from: _texture }));
  };

  const update = useCallback(
    (
      _transition: string,
      _reverse: boolean,
      _image: any,
      _motionTransition?: {
        type: string;
        stiffness?: number;
        damping?: number;
        duration?: number;
      },
    ): void => {
      setState((s) => ({
        ...s,
        transition: _transition,
        reverse: _reverse,
        texture: _image,
        motionTransition: _motionTransition,
      }));
    },
    [],
  );

  return update;
}
